// ---------------------------------------------------------------------------
// DSS-19: Scroll effects builder — parallax, fade, blur, rotate, scale
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { ArrowDownUp, X } from "lucide-react";
import {
  DEFAULT_SCROLL_CONFIG,
  MIN_SPEED, MAX_SPEED,
  MIN_VIEWPORT, MAX_VIEWPORT,
  MIN_BLUR, MAX_BLUR,
  MIN_SCALE, MAX_SCALE,
  safeNum,
  type ScrollEffectsConfig,
  type VerticalScrollEffect,
  type HorizontalScrollEffect,
  type TransparencyEffect,
  type BlurEffect,
  type RotateEffect,
  type ScaleEffect,
  type ScrollDirection,
  type FadeDirection,
  type RotateDirection,
  type ScaleDirection,
} from "./scroll-effects-config";

// Re-export for consumers
export type { ScrollEffectsConfig } from "./scroll-effects-config";
export { generateScrollDataAttributes } from "./scroll-effects-config";

// ── Props ──
interface ScrollEffectsProps {
  readonly value: ScrollEffectsConfig | null;
  readonly onChange: (config: ScrollEffectsConfig | null) => void;
}

// ── Component ──
export default function ScrollEffects({ value, onChange }: ScrollEffectsProps) {
  const uid = useId();
  const config = value ?? DEFAULT_SCROLL_CONFIG;
  const isEnabled = value !== null;

  const handleToggle = useCallback(() => {
    onChange(isEnabled ? null : { ...DEFAULT_SCROLL_CONFIG });
  }, [isEnabled, onChange]);

  const handleClear = useCallback(() => onChange(null), [onChange]);

  const updateVertical = useCallback(
    (patch: Partial<VerticalScrollEffect>) => onChange({ ...config, vertical: { ...config.vertical, ...patch } }),
    [config, onChange],
  );
  const updateHorizontal = useCallback(
    (patch: Partial<HorizontalScrollEffect>) => onChange({ ...config, horizontal: { ...config.horizontal, ...patch } }),
    [config, onChange],
  );
  const updateTransparency = useCallback(
    (patch: Partial<TransparencyEffect>) => onChange({ ...config, transparency: { ...config.transparency, ...patch } }),
    [config, onChange],
  );
  const updateBlur = useCallback(
    (patch: Partial<BlurEffect>) => onChange({ ...config, blur: { ...config.blur, ...patch } }),
    [config, onChange],
  );
  const updateRotate = useCallback(
    (patch: Partial<RotateEffect>) => onChange({ ...config, rotate: { ...config.rotate, ...patch } }),
    [config, onChange],
  );
  const updateScale = useCallback(
    (patch: Partial<ScaleEffect>) => onChange({ ...config, scale: { ...config.scale, ...patch } }),
    [config, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
          <ArrowDownUp className="h-3.5 w-3.5" />
          Scroll Effects
        </div>
        <div className="flex items-center gap-2">
          {isEnabled && (
            <button type="button" onClick={handleClear} className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]">
              <X className="h-3 w-3" /> Clear
            </button>
          )}
          <ToggleSwitch isEnabled={isEnabled} onToggle={handleToggle} />
        </div>
      </div>

      {isEnabled && (
        <div className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
          <EffectSection label="Vertical Scroll" isEnabled={config.vertical.enabled} onToggle={(v) => updateVertical({ enabled: v })}>
            <SliderRow uid={uid} label="Speed" value={config.vertical.speed} min={MIN_SPEED} max={MAX_SPEED} suffix="" onChange={(v) => updateVertical({ speed: v })} />
            <SegmentedControl options={[{ value: "up", label: "Up" }, { value: "down", label: "Down" }]} value={config.vertical.direction} onChange={(v) => updateVertical({ direction: v as ScrollDirection })} />
          </EffectSection>

          <EffectSection label="Horizontal Scroll" isEnabled={config.horizontal.enabled} onToggle={(v) => updateHorizontal({ enabled: v })}>
            <SliderRow uid={uid} label="Speed" value={config.horizontal.speed} min={MIN_SPEED} max={MAX_SPEED} suffix="" onChange={(v) => updateHorizontal({ speed: v })} />
          </EffectSection>

          <EffectSection label="Transparency" isEnabled={config.transparency.enabled} onToggle={(v) => updateTransparency({ enabled: v })}>
            <SegmentedControl options={[{ value: "in", label: "Fade In" }, { value: "out", label: "Fade Out" }]} value={config.transparency.direction} onChange={(v) => updateTransparency({ direction: v as FadeDirection })} />
            <ViewportRange uid={uid} start={config.transparency.viewportStart} end={config.transparency.viewportEnd} onStartChange={(v) => updateTransparency({ viewportStart: v })} onEndChange={(v) => updateTransparency({ viewportEnd: v })} />
          </EffectSection>

          <EffectSection label="Blur on Scroll" isEnabled={config.blur.enabled} onToggle={(v) => updateBlur({ enabled: v })}>
            <SliderRow uid={uid} label="Max blur" value={config.blur.maxBlur} min={MIN_BLUR} max={MAX_BLUR} suffix="px" onChange={(v) => updateBlur({ maxBlur: v })} />
            <ViewportRange uid={uid} start={config.blur.viewportStart} end={config.blur.viewportEnd} onStartChange={(v) => updateBlur({ viewportStart: v })} onEndChange={(v) => updateBlur({ viewportEnd: v })} />
          </EffectSection>

          <EffectSection label="Rotate on Scroll" isEnabled={config.rotate.enabled} onToggle={(v) => updateRotate({ enabled: v })}>
            <SegmentedControl options={[{ value: "cw", label: "CW" }, { value: "ccw", label: "CCW" }]} value={config.rotate.direction} onChange={(v) => updateRotate({ direction: v as RotateDirection })} />
            <SliderRow uid={uid} label="Speed" value={config.rotate.speed} min={MIN_SPEED} max={MAX_SPEED} suffix="" onChange={(v) => updateRotate({ speed: v })} />
            <ViewportRange uid={uid} start={config.rotate.viewportStart} end={config.rotate.viewportEnd} onStartChange={(v) => updateRotate({ viewportStart: v })} onEndChange={(v) => updateRotate({ viewportEnd: v })} />
          </EffectSection>

          <EffectSection label="Scale on Scroll" isEnabled={config.scale.enabled} onToggle={(v) => updateScale({ enabled: v })}>
            <SegmentedControl options={[{ value: "grow", label: "Grow" }, { value: "shrink", label: "Shrink" }]} value={config.scale.direction} onChange={(v) => updateScale({ direction: v as ScaleDirection })} />
            <div className="flex items-center gap-2">
              <span className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">Range</span>
              <input type="number" min={MIN_SCALE} max={MAX_SCALE} step={0.1} value={config.scale.rangeMin} onChange={(e) => updateScale({ rangeMin: safeNum(Number(e.target.value), MIN_SCALE, MAX_SCALE) })} className="h-6 w-14 rounded border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 text-[10px] tabular-nums text-[var(--color-foreground)]" />
              <span className="text-[10px] text-[var(--color-muted-foreground)]">to</span>
              <input type="number" min={MIN_SCALE} max={MAX_SCALE} step={0.1} value={config.scale.rangeMax} onChange={(e) => updateScale({ rangeMax: safeNum(Number(e.target.value), MIN_SCALE, MAX_SCALE) })} className="h-6 w-14 rounded border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 text-[10px] tabular-nums text-[var(--color-foreground)]" />
            </div>
            <ViewportRange uid={uid} start={config.scale.viewportStart} end={config.scale.viewportEnd} onStartChange={(v) => updateScale({ viewportStart: v })} onEndChange={(v) => updateScale({ viewportEnd: v })} />
          </EffectSection>
        </div>
      )}
    </div>
  );
}

// ── Toggle switch ──
function ToggleSwitch({ isEnabled, onToggle }: { readonly isEnabled: boolean; readonly onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} role="switch" aria-checked={isEnabled} className={["relative h-5 w-9 rounded-full border transition-colors", isEnabled ? "border-[var(--color-primary)] bg-[var(--color-primary)]" : "border-[var(--color-border)] bg-[var(--color-muted)]"].join(" ")}>
      <span className={["absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform", isEnabled ? "translate-x-4" : "translate-x-0.5"].join(" ")} />
    </button>
  );
}

// ── Effect section with mini toggle ──
function EffectSection({ label, isEnabled, onToggle, children }: { readonly label: string; readonly isEnabled: boolean; readonly onToggle: (enabled: boolean) => void; readonly children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-[var(--color-foreground)]">{label}</span>
        <button type="button" onClick={() => onToggle(!isEnabled)} role="switch" aria-checked={isEnabled} className={["relative h-4 w-7 rounded-full border transition-colors", isEnabled ? "border-[var(--color-primary)] bg-[var(--color-primary)]" : "border-[var(--color-border)] bg-[var(--color-muted)]"].join(" ")}>
          <span className={["absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white shadow-sm transition-transform", isEnabled ? "translate-x-3" : "translate-x-0.5"].join(" ")} />
        </button>
      </div>
      {isEnabled && <div className="flex flex-col gap-2 pl-2">{children}</div>}
    </div>
  );
}

// ── Slider row ──
function SliderRow({ uid, label, value, min, max, suffix, onChange }: { readonly uid: string; readonly label: string; readonly value: number; readonly min: number; readonly max: number; readonly suffix: string; readonly onChange: (val: number) => void }) {
  const id = `${uid}-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">{label}</label>
      <input id={id} type="range" min={min} max={max} step={1} value={value} onChange={(e) => onChange(safeNum(Number(e.target.value), min, max))} className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]" />
      <span className="w-10 text-right text-[10px] tabular-nums text-[var(--color-muted-foreground)]">{value}{suffix}</span>
    </div>
  );
}

// ── Segmented control ──
function SegmentedControl({ options, value, onChange }: { readonly options: readonly { readonly value: string; readonly label: string }[]; readonly value: string; readonly onChange: (value: string) => void }) {
  return (
    <div className="flex rounded-md border border-[var(--color-border)] p-0.5">
      {options.map((opt) => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)} className={["flex-1 rounded-sm px-3 py-1 text-[10px] font-medium transition-colors", value === opt.value ? "bg-[var(--color-primary)] text-white" : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"].join(" ")}>{opt.label}</button>
      ))}
    </div>
  );
}

// ── Viewport range inputs ──
function ViewportRange({ uid, start, end, onStartChange, onEndChange }: { readonly uid: string; readonly start: number; readonly end: number; readonly onStartChange: (val: number) => void; readonly onEndChange: (val: number) => void }) {
  const startId = `${uid}-vp-s-${start}`;
  const endId = `${uid}-vp-e-${end}`;
  return (
    <div className="flex items-center gap-2">
      <span className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">Viewport</span>
      <label htmlFor={startId} className="sr-only">Viewport start</label>
      <input id={startId} type="number" min={MIN_VIEWPORT} max={MAX_VIEWPORT} value={start} onChange={(e) => onStartChange(safeNum(Number(e.target.value), MIN_VIEWPORT, MAX_VIEWPORT))} className="h-6 w-12 rounded border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 text-[10px] tabular-nums text-[var(--color-foreground)]" />
      <span className="text-[10px] text-[var(--color-muted-foreground)]">% to</span>
      <label htmlFor={endId} className="sr-only">Viewport end</label>
      <input id={endId} type="number" min={MIN_VIEWPORT} max={MAX_VIEWPORT} value={end} onChange={(e) => onEndChange(safeNum(Number(e.target.value), MIN_VIEWPORT, MAX_VIEWPORT))} className="h-6 w-12 rounded border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 text-[10px] tabular-nums text-[var(--color-foreground)]" />
      <span className="text-[10px] text-[var(--color-muted-foreground)]">%</span>
    </div>
  );
}
